import { useEffect } from 'react';

/**
 * Congela la página mientras hay una capa a pantalla completa encima.
 *
 * ── Por qué lleva CUENTA y no guarda el valor anterior ──────────────────────
 *
 * Esto guardaba el `overflow` de antes y lo restauraba al salir. Parece la
 * forma correcta de anidar y es justo lo que rompe la app entera:
 *
 *   1. Se abre el panel de cliente  → guarda '', pone 'hidden'
 *   2. Encima se abre un modal      → guarda 'hidden', pone 'hidden'
 *   3. Se cierra el PANEL primero   → restaura ''
 *   4. Se cierra el modal           → restaura 'hidden'   ← y aquí muere
 *
 * A partir del paso 4 no se puede deslizar en ninguna pantalla hasta recargar,
 * y no hay nada en la interfaz que explique por qué. El orden LIFO no está
 * garantizado: basta con que una ruta cambie, con que React desmonte los dos
 * en el mismo commit, o con cerrar el panel desde debajo del modal.
 *
 * Con una cuenta no hay orden que valga: el primero que entra bloquea, el
 * último que sale desbloquea, y lo que pase en medio da igual.
 *
 * Se ponen `documentElement` Y `body`: los navegadores no coinciden en cuál
 * manda el scroll, y bloquear solo uno deja el otro suelto en algunos motores.
 *
 * @param enabled `false` mantiene el hook montado sin bloquear — los hooks no
 *                se pueden llamar condicionalmente, así que quien bloquea solo
 *                a veces necesita una bandera y no un `if`.
 */

/** Cuántas capas piden el bloqueo ahora mismo. */
let capas = 0;
/** El relleno que se puso para compensar la barra, para poder quitarlo igual. */
let rellenoPuesto = '';

function bloquear() {
    capas += 1;
    if (capas > 1) return;   // ya estaba bloqueado; solo se anota

    const raiz = document.documentElement;
    // Al ocultar el desbordamiento desaparece la barra de scroll, y con ella se
    // van sus ~15px de ancho: la página de debajo se ensancha de golpe y todo
    // salta a la derecha justo cuando aparece el modal. Se rellena ese hueco
    // para que nada se mueva. En móvil y en macOS con barras superpuestas el
    // ancho es 0 y no se toca nada.
    const anchoBarra = window.innerWidth - raiz.clientWidth;
    rellenoPuesto = anchoBarra > 0 ? `${anchoBarra}px` : '';

    raiz.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    if (rellenoPuesto) document.body.style.paddingRight = rellenoPuesto;
}

function desbloquear() {
    capas = Math.max(0, capas - 1);
    if (capas > 0) return;   // todavía queda alguna capa abierta

    // Se QUITAN las propiedades en vez de restaurar un valor guardado: la
    // página no lleva `overflow` en línea por su cuenta, así que quitarlo la
    // devuelve exactamente a su estado de hoja de estilos. Guardar y restaurar
    // es lo que permitía que un valor viejo —'hidden'— sobreviviera al cierre.
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    rellenoPuesto = '';
}

export function useBodyScrollLock(enabled: boolean = true): void {
    useEffect(() => {
        if (!enabled) return;
        if (typeof document === 'undefined') return;
        bloquear();
        return desbloquear;
    }, [enabled]);
}

/**
 * El mismo bloqueo, pero AUTOMÁTICO para cualquier capa a pantalla completa.
 *
 * Se reportó así: «cuando abro este tipo de ventanas, si muevo el scroll
 * scrollea lo del fondo». Y no era de un modal: son 39 los archivos del repo que
 * montan una capa `fixed inset-0` y solo cuatro llamaban al hook. Ponerlo a mano
 * en los 39 exige acertar con todos hoy Y que nadie escriba el 40 mañana; ya se
 * intentó ese camino con los márgenes de las cortinas y se escapó la mitad.
 *
 * Así que en vez de pedir que cada modal se acuerde, se mira el DOM: mientras
 * exista alguna capa a pantalla completa VISIBLE, la página no se mueve.
 *
 * El selector es de CLASES de Tailwind (`.fixed.inset-0`), no de estilo
 * calculado, y eso es a propósito: es exactamente el patrón con el que esta app
 * escribe sus cortinas, y deja fuera cosas que llevan `position: fixed` por
 * otros motivos y no tapan nada — el contenedor de los avisos (que va a
 * `inset: 16px` por estilo en línea), la insignia de sin-conexión, el botón
 * flotante del asistente.
 *
 * Se comprueba que TAPE de verdad: una capa con `display:none`, sin tamaño o
 * con `pointer-events:none` (decoración) no cuenta. Sin esto, una cortina que
 * React deja montada pero oculta dejaría la app trabada sin nada en pantalla
 * que lo explicara.
 *
 * Sigue conviviendo con las llamadas manuales: como el bloqueo lleva CUENTA, un
 * modal que además llame al hook simplemente suma y resta su capa.
 */
export function useBloqueoAutomaticoDeScroll(): void {
    useEffect(() => {
        if (typeof document === 'undefined') return;

        const tapaLaPantalla = (el: Element) => {
            const cs = getComputedStyle(el);
            if (cs.display === 'none' || cs.visibility === 'hidden') return false;
            if (cs.pointerEvents === 'none') return false;
            const r = el.getBoundingClientRect();
            return r.width > 0 && r.height > 0;
        };

        // `bloqueado` es de este vigilante, no la cuenta global: así solo suma
        // UNA capa por muchas cortinas que haya a la vez, y la resta una vez.
        let bloqueado = false;
        const revisar = () => {
            const hay = [...document.querySelectorAll('.fixed.inset-0')].some(tapaLaPantalla);
            if (hay === bloqueado) return;
            bloqueado = hay;
            if (hay) bloquear(); else desbloquear();
        };

        revisar();
        const vigia = new MutationObserver(revisar);
        vigia.observe(document.body, {
            childList: true,
            subtree: true,
            // También por atributos: una cortina que ya está montada y solo
            // cambia de clase —de `hidden` a visible— no mueve el árbol.
            attributes: true,
            attributeFilter: ['class', 'style'],
        });

        return () => {
            vigia.disconnect();
            if (bloqueado) desbloquear();
        };
    }, []);
}

/**
 * Escape cierra la ventana de encima. Para toda la app, en un solo sitio.
 *
 * Se probó pulsando Escape en los modales reales —Empezar un trabajo, Nuevo
 * prospecto, Proyecto nuevo, Agregar equipo— y NINGUNO cerraba. Cerrar con
 * Escape es de las tres cosas que todo el mundo intenta ante una ventana
 * (la X, pulsar fuera, y Escape), y era la única que no estaba.
 *
 * El truco para no tocar 18 componentes: no se busca su función de cerrar —
 * se le manda un CLIC A LA CORTINA. Todas cierran ya al pulsar fuera, con la
 * guarda `e.target === e.currentTarget`, así que un clic sintético sobre la
 * cortina entra por ese mismo camino y usa el cierre que cada una ya tiene.
 *
 * De regalo, respeta las que NO deben cerrarse solas: el editor de estimados,
 * el flujo de firma y el asistente inicial no tienen manejador en su cortina,
 * así que el clic no hace nada y Escape tampoco. Sin listas que mantener.
 *
 * Se cierra SOLO LA DE ENCIMA (mayor `z-index`): con un menú abierto dentro de
 * un panel, Escape debe cerrar el menú, no llevarse el panel por delante.
 */
export function useCerrarConEscape(): void {
    useEffect(() => {
        if (typeof document === 'undefined') return;

        const alPulsar = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || e.defaultPrevented) return;

            const capas = [...document.querySelectorAll<HTMLElement>('.fixed.inset-0')]
                .filter((el) => {
                    const cs = getComputedStyle(el);
                    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
                    if (cs.pointerEvents === 'none') return false;
                    const r = el.getBoundingClientRect();
                    return r.width > 200 && r.height > 200;
                });
            if (!capas.length) return;

            const arriba = capas.reduce((a, b) =>
                (parseInt(getComputedStyle(b).zIndex, 10) || 0) >= (parseInt(getComputedStyle(a).zIndex, 10) || 0) ? b : a,
            );

            /* Dos formas de fondo clicable conviven en la app y hay que probar
               las dos, o los paneles laterales se quedaban sin Escape:
                 - MODALES: la cortina misma cierra (guarda `e.target ===
                   e.currentTarget`), así que se pulsa la cortina.
                 - PANELES de detalle (cliente, estimado, prospecto): la cortina
                   es solo el contenedor, y quien cierra es un `<button>` de
                   fondo a tamaño completo dentro. Pulsar el contenedor no hace
                   nada; hay que pulsar ese botón.
               Se busca primero el botón de fondo; si no hay, la cortina. */
            const fondo = arriba.querySelector<HTMLElement>(
                ':scope > button[class*="absolute"][class*="inset-0"], :scope > [class*="absolute"][class*="inset-0"]',
            );
            (fondo ?? arriba).dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        };

        window.addEventListener('keydown', alPulsar);
        return () => window.removeEventListener('keydown', alPulsar);
    }, []);
}

export default useBodyScrollLock;
